import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter } from '@patsnap/tahiti-core/common/event';
import type { SSEResponse, SSEStreamService } from '@patsnap-utils/sse';
import { AITranslationModel } from '../model/ai-translation.model';
import { Step } from '../type/ai-translation.enum';
import type { AITranslationState, TranslationOriginalRequest, ContentItem } from '../type/ai-translation.type';
import { TranslationResultService } from '@/services/ai-translation/service/translation-result.service';
import { TranslationOriginalDTO } from '@/services/ai-translation/service/ai-translation.repository.interface';
import { type ApiTranslationOriginalResponse, TranslationOriginal } from '@/infrastructure/api-functions/interface/translation-original.interface';
import { StreamStatus } from '@/services/ai-translation/type/ai-translation.enum';
import { StreamService, StreamEvents } from '@/services/ai-translation/service/stream.service.interface'

@injectable()
export class SectionTypewriterServiceImpl implements StreamService<string> {
    private _stream: SSEStreamService<any> | null = null;
    private _onStatusChangeEmitter = new Emitter<string>();
    readonly onStatusChange = this._onStatusChangeEmitter.event;
    private _onContentChangeEmitter = new Emitter<string>();
    readonly onContentChange = this._onContentChangeEmitter.event;

    private _onStatesChangeEmitter = new Emitter<AITranslationState>();
    readonly onStatesChange = this._onStatesChangeEmitter.event;

    constructor(
        @inject(AITranslationModel)
        private _aiTranslationModel: AITranslationModel,
        @inject(TranslationResultService)
        private _translationResultService: TranslationResultService,
        @inject(TranslationOriginal)
        private _translationOriginal: TranslationOriginal,
        @inject(TranslationOriginalDTO)
        private _translationOriginalDTO: TranslationOriginalDTO,
    ) {
    }

    public status = StreamStatus.PROGRESS_WITHOUT_DATA;

    private _fetchStreamData(input: TranslationOriginalRequest, streamEvents?: StreamEvents<ApiTranslationOriginalResponse>): void {
        let newTranslation = '';
        // this._stream = this._translationOriginalDTO.fetch<T>(input)
        this._stream = this._translationOriginal.translationOriginal({
            current_step: input.currentStep,
            task_id: input.taskId,
            original_index: input.originalIndex,
            original_text: input.originalText,
        })
            .onOpen((response: Response) => {
                streamEvents?.onOpen && streamEvents.onOpen(response);
            })
            .onMessage((data: SSEResponse<ApiTranslationOriginalResponse>) => {
                const { status, content = '' } = data?.data ?? {};
                this.status = status;
                this._onStatusChangeEmitter.fire(status);
                if (status === StreamStatus.FINISH) {
                    this._stream?.close();

                    this._translationResultService.updateTranslation(
                        newTranslation,
                        input.originalIndex,
                        false,
                    );
                } else {
                    newTranslation = content;
                    this._onContentChangeEmitter.fire(content);
                }
            })
            .onError((err: any) => {
                streamEvents?.onError && streamEvents.onError(err);
            })
            .onClose(() => {
                streamEvents?.onClose && streamEvents.onClose();
            });

        this._stream?.start();
    }

    private _initParams = {};
    public execute(initParams?: {
        originalIndex: number;
        originalText: string;
    }) {
        if (initParams) {
            this._initParams = initParams;
        }
        this.status = StreamStatus.PROGRESS_WITHOUT_DATA;
        this._onStatusChangeEmitter.fire(this.status);

        const aiTranslationModel = this._aiTranslationModel.modelData;

        this._fetchStreamData({
            currentStep: Step.RESULT,
            taskId: aiTranslationModel.taskId,
            ...this._initParams,
        } as TranslationOriginalRequest);
    }

    public abort() {
        this.status = StreamStatus.ABORTED;
        this._onStatusChangeEmitter.fire(this.status);
        this._stream?.close();
    }

    public executeRegenerate() {
        this.abort();
        this._onContentChangeEmitter.fire('');
        this.execute();
    }

    public stopLoading() {
        this.abort();
    }
}